import { useCallback, useState } from 'react';

import { useInputValue } from '../../../hooks';
import { useVerifyLoginMfa } from '../../../hooks/api/auth/mfa/useVerifyLoginMfa';
import { hardRedirectTo } from '../../../utils';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { RouterProps } from '../../../types';

import type { AuthInput, LoginStep } from './AuthContext';

type StatusMethods = {
  onPending: () => void;
  onSuccess: () => void;
  onError: (failedResponse: FailedFetchResponse) => string;
};

type UseMfaFlowParams = {
  statusMethods: StatusMethods;
  onPasswordChange: (value: string) => void;
  setStep: (step: LoginStep) => void;
  setAlert: (alert: string | null) => void;
};

type UseMfaFlowReturn = {
  mfaCode: AuthInput;
  mfaMethod: MfaMethodType | null;
  useBackupCode: boolean;
  toggleBackupCode: () => void;
  onVerifyMfa: (loc: RouterProps['location'], code: string, isBackupCode: boolean) => Promise<void>;
  setMfaMethod: (method: MfaMethodType | null) => void;
  resetMfaState: () => void;
};

/**
 * Custom hook that encapsulates all MFA-related state and logic for the auth flow.
 * This includes MFA code input, backup code toggle, and verification handling.
 */
const useMfaFlow = ({
  statusMethods,
  onPasswordChange,
  setStep,
  setAlert,
}: UseMfaFlowParams): UseMfaFlowReturn => {
  const [mfaCode, onMfaCodeChange] = useInputValue('');
  const [mfaMethod, setMfaMethod] = useState<MfaMethodType | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const verifyMfaMutation = useVerifyLoginMfa();

  const toggleBackupCode = useCallback(() => {
    setUseBackupCode(prev => !prev);
    onMfaCodeChange('');
  }, [onMfaCodeChange]);

  const resetMfaState = useCallback(() => {
    onMfaCodeChange('');
    setMfaMethod(null);
    setUseBackupCode(false);
  }, [onMfaCodeChange]);

  const onVerifyMfa = useCallback(async (
    loc: RouterProps['location'],
    code: string,
    isBackupCode: boolean,
  ) => {
    statusMethods.onPending();
    setAlert(null);

    // Normalize code: strip hyphens and whitespace
    const normalizedCode = code.replace(/[-\s]/g, '');

    try {
      await verifyMfaMutation.mutateAsync({
        code: normalizedCode,
        isBackupCode,
      });

      // On success, user is logged in. Redirect to the next page.
      const nextRedirectValue = loc.query.next || '/';
      hardRedirectTo(nextRedirectValue);
    } catch (error) {
      // Check if this is a session-related error (rate limit or expired session)
      // These errors require the user to restart the login process
      const errorData = error as { data?: { message?: string }; message?: string };
      const errorMessage = errorData?.data?.message || errorData?.message || '';
      const isSessionError = errorMessage.includes('Too many failed MFA attempts') ||
        errorMessage.includes('MFA session expired') ||
        errorMessage.includes('No active MFA session');

      if (isSessionError) {
        // Reset to login page when session is invalid
        setStep('enterEmail');
        onMfaCodeChange('');
        onPasswordChange('');
        setMfaMethod(null);
        setUseBackupCode(false);
        statusMethods.onError(error as FailedFetchResponse);
      } else {
        // For all other errors (invalid code, network errors, etc.)
        // Clear the MFA code so user can try again
        onMfaCodeChange('');
        statusMethods.onError(error as FailedFetchResponse);
      }
    }
  }, [
    statusMethods,
    setAlert,
    verifyMfaMutation,
    setStep,
    onMfaCodeChange,
    onPasswordChange,
  ]);

  return {
    mfaCode: {
      ref: null,
      value: mfaCode,
      onChange: onMfaCodeChange,
    },
    mfaMethod,
    useBackupCode,
    toggleBackupCode,
    onVerifyMfa,
    setMfaMethod,
    resetMfaState,
  };
};

export default useMfaFlow;
